import { toIntlLocale } from "@/i18n/to-intl-locale";
import { toDayOfWeek, toLongDate, toShortDate } from "@/shared/utils/date";
import { monthNumberToName } from "@/shared/utils/month-number-to-name";
import type { GetControlResultDetailsItemDtoData } from "@/spz-api/model";
import { formatCurrency } from "@spz/ui/components/table/cells/amount-cell";
import type { TFunction, i18n } from "i18next";
import { formatListOfValues } from "./format-list-of-values";

export const formatCellValue = (
  data: GetControlResultDetailsItemDtoData,
  itemType: string,
  accessorKey: string,
  t: TFunction,
  i18n: Pick<i18n, "language">,
  minorUnit?: number,
  currency?: string,
): string | number | null => {
  const value = data[accessorKey] as string | number | null;
  if (["libelle_ml", "libellé_ml", "combined_ml_text"].includes(itemType)) {
    return typeof value === "string" ? t(value) : value;
  }
  if (itemType === "short_date") {
    return value ? toShortDate(new Date(value), i18n.language) : null;
  }
  if (itemType === "long_date") {
    return value ? toLongDate(new Date(value), i18n.language) : null;
  }
  if (itemType === "day_of_week") {
    return value ? toDayOfWeek(new Date(value), i18n.language) : null;
  }
  if (itemType === "monetary") {
    if (!currency || !value) return "";
    return formatCurrency({
      amount: typeof value === "number" ? value : Number(value),
      minorUnit: minorUnit ?? 1,
      currency: currency ?? "USD",
    });
  }
  if (
    itemType === "list_of_values" ||
    String(accessorKey).startsWith("list_of_")
  ) {
    return String(formatListOfValues(value));
  }
  if (itemType === "date_month") {
    if (!value) return null;

    const monthNumber = typeof value === "number" ? value : Number(value);

    return monthNumberToName(monthNumber, i18n.language, { month: "long" });
  }
  if (itemType === "percentage_with_two_decimals") {
    return Intl.NumberFormat(toIntlLocale(i18n.language), {
      style: "percent",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  }
  if (itemType === "percentage_with_three_decimals") {
    return Intl.NumberFormat(toIntlLocale(i18n.language), {
      style: "percent",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Number(value));
  }
  if (itemType === "percentage") {
    return Intl.NumberFormat(toIntlLocale(i18n.language), {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value));
  }
  return value;
};
